import createPublishingPermissionService from '../services/createPublishingPermissionService';
import CalendarPublishingError from '../store/schema/CalendarPublishingError';
import { CalendarPublishingState } from '../store/schema/CalendarPublishingStore';
import getStore from 'owa-calendar-sharing/lib/store/publishingStore';
import SetCalendarPublishingResponse from 'owa-service/lib/contract/SetCalendarPublishingResponse';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { orchestrator } from 'satcheljs';
import {
    resetCalendarPublishingPermissionLinks,
    setLinkResetPending,
    setCalendarPublishingCalendarError,
    saveCalendarPublishingPermissions,
    loadCalendarPublishingPermissions,
} from '../actions/calendarPublishing';

export default orchestrator(resetCalendarPublishingPermissionLinks, async msg => {
    let folderId = msg.folderId;
    let viewState = getStore().viewState;
    let oldDetailLevel = msg.detailLevel;

    setCalendarPublishingCalendarError(folderId, null); // Reset publishing error

    setLinkResetPending(folderId, oldDetailLevel, true);

    let data: SetCalendarPublishingResponse = null;
    if (isConsumer()) {
        data = await resetConsumerLinks(folderId, oldDetailLevel);
    } else {
        data = await resetEnterpriseLinks(folderId, oldDetailLevel);
    }

    if (data) {
        if (isConsumer()) {
            processConsumerResponse(viewState, folderId, data);
        } else {
            processEnterpriseResponse(viewState, folderId, data);
        }
    } else {
        setCalendarPublishingCalendarError(folderId, CalendarPublishingError.ResetLinksFailure);
    }
    setLinkResetPending(folderId, oldDetailLevel, false);
});

async function resetConsumerLinks(
    folderId: string,
    oldDetailLevel: string
): Promise<SetCalendarPublishingResponse> {
    return createPublishingPermissionService(
        folderId,
        oldDetailLevel,
        true /* publish */,
        true /* reset links */
    );
}

async function resetEnterpriseLinks(
    folderId: string,
    oldDetailLevel: string
): Promise<SetCalendarPublishingResponse> {
    // although there is a resetLinks param, for enterprise users this call will fail. To reset links, need to
    // explicitly disable links, then make call to re-enable them
    let data: SetCalendarPublishingResponse = await createPublishingPermissionService(
        folderId,
        'Disabled',
        false,
        false
    );

    if (!data) {
        return null;
    }

    return createPublishingPermissionService(
        folderId,
        oldDetailLevel,
        true /* publish */,
        false /* reset links */
    );
}

function processConsumerResponse(
    viewState: CalendarPublishingState,
    folderId: string,
    data: SetCalendarPublishingResponse
) {
    // in consumer the data returned has null for all values on a success call, so we need to explicitly call a refresh
    let updatedCalendar = { ...viewState.publishedCalendarsMap.get(folderId) };
    loadCalendarPublishingPermissions(
        folderId,
        '',
        updatedCalendar.primaryColor,
        updatedCalendar.calName
    );
}

function processEnterpriseResponse(
    viewState: CalendarPublishingState,
    folderId: string,
    data: SetCalendarPublishingResponse
) {
    let { BrowseUrl, ICalUrl, CurrentDetailLevel } = data;

    // break the observables on the shallow copy before updating it in the save
    let updatedCalendar = { ...viewState.publishedCalendarsMap.get(folderId) };
    updatedCalendar.itemStates = updatedCalendar.itemStates.map(itemState => {
        let newState = { ...itemState };
        newState.CurrentDetailLevel = CurrentDetailLevel;
        newState.BrowseUrl = BrowseUrl;
        newState.ICalUrl = ICalUrl;
        return newState;
    });

    saveCalendarPublishingPermissions(
        updatedCalendar.itemStates,
        folderId,
        updatedCalendar.calName,
        updatedCalendar.primaryColor
    );
}
